// EditorFrame — the CKEditor half of the document frame. Wraps
// TemplateEditor in a `.doc-frame` and appends the in-frame pager, so
// every edit surface (template draft, deal document, template author)
// gets the same reactive shell: the editor fills the frame, its toolbar
// is sticky, its editable is the single scroller, and a ‹ Prev / Next ›
// pager sits in the footer (Rule 3). See DocPager + useEditorPager and
// doc-frame.css. In a `.doc-route` the frame flexes to fill the viewport;
// pass `bounded` when used inside a scrolling multi-panel layout.
import { useState } from "react";
import type { ReactNode } from "react";
import { TemplateEditor, type TemplateEditorProps } from "./TemplateEditor";
import { DocPager } from "./DocPager";
import { useEditorPager, type PagerEditor } from "./useEditorPager";

export interface EditorFrameProps extends TemplateEditorProps {
  /** Left-aligned status text in the pager footer (e.g. "draft · unsaved"). */
  pagerMeta?: ReactNode;
  /** Cap the frame height instead of viewport-filling — for editors inside
   *  a scrolling multi-panel layout rather than a full-height .doc-route. */
  bounded?: boolean;
  className?: string;
}

export function EditorFrame({
  pagerMeta,
  bounded = false,
  className,
  onReady,
  value,
  ...rest
}: EditorFrameProps): JSX.Element {
  const [editor, setEditor] = useState<PagerEditor | null>(null);
  const pager = useEditorPager(editor, value);

  const frameClass = ["doc-frame", bounded ? "doc-frame--bounded" : "", className]
    .filter((c): c is string => typeof c === "string" && c.length > 0)
    .join(" ");

  return (
    <div className={frameClass}>
      <TemplateEditor
        {...rest}
        value={value}
        onReady={(ed) => {
          setEditor(ed as PagerEditor);
          onReady?.(ed);
        }}
      />
      <DocPager
        page={pager.page}
        total={pager.total}
        onPrev={pager.prev}
        onNext={pager.next}
        meta={pagerMeta}
        testId="doc-pager"
      />
    </div>
  );
}
